import { useState, useEffect, useRef } from 'react'
import { Label } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Loader2, RefreshCw, Upload, User, X, Star } from 'lucide-react'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useConsultationStore } from '../../../../stores/consultationStore'
import { apiClient } from '../../../../services/api'
import type { PersonaSettings } from '@creanizer/types'

// デフォルトパラメータ
const DEFAULT_SETTINGS: PersonaSettings['settings'] = {
  personalityStrength: 7,
  formality: 5,
  creativity: 7,
  empathy: 8,
  subjectivity: 7
}

export function PersonaSelector() {
  const consultationStore = useConsultationStore()
  const characterIcons = useConsultationStore(state => state.characterIcons)
  const isStoreHydrated = useConsultationStore(state => state.isHydrated)
  const characterStore = useCharacterStore()
  const [selectedCharacterId, setSelectedCharacterId] = useState<string>('none')
  const [personaEnabled, setPersonaEnabled] = useState(false)
  const [maintainPerspective, setMaintainPerspective] = useState(true)
  const [settings, setSettings] = useState<PersonaSettings['settings']>({
    personalityStrength: 7,
    formality: 5,
    creativity: 7,
    empathy: 8,
    subjectivity: 7
  })
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisReasoning, setAnalysisReasoning] = useState<Record<string, string> | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [characterIcon, setCharacterIcon] = useState<string | null>(null)
  
  useEffect(() => {
    if (!isStoreHydrated) {
      console.log('Waiting for store hydration...')
      return
    }
    
    console.log('Store hydrated, restoring persona settings:', {
      hasPersonaSettings: !!consultationStore.personaSettings,
      hasCharacterIcons: Object.keys(consultationStore.characterIcons || {}).length > 0
    })
    
    // 既存のペルソナ設定がある場合は復元
    if (consultationStore.personaSettings && !isInitialized) {
      const characterId = consultationStore.personaSettings.characterId
      setSelectedCharacterId(characterId)
      setPersonaEnabled(consultationStore.personaSettings.enabled)
      setMaintainPerspective(consultationStore.personaSettings.maintainPerspective ?? true)
      
      // アイコンを復元
      if (characterId !== 'none') {
        const icon = consultationStore.getCharacterIcon(characterId)
        setCharacterIcon(icon)
      }
      
      // 既存の設定を優先的に使用
      if (consultationStore.personaSettings.settings) {
        // personaSettingsに設定があればそれを使用
        setSettings(consultationStore.personaSettings.settings)
        // キャッシュから理由を取得
        const cached = consultationStore.getCachedAnalysis(characterId)
        if (cached) {
          setAnalysisReasoning(cached.reasoning)
        }
      } else if (characterId !== 'none') {
        // 設定がない場合のみキャッシュを確認
        const cached = consultationStore.getCachedAnalysis(characterId)
        if (cached) {
          setSettings(cached.settings)
          setAnalysisReasoning(cached.reasoning)
        } else {
          // キャッシュもなければデフォルト値
          setSettings(DEFAULT_SETTINGS)
        }
      }
      setIsInitialized(true)
    } else if (!isInitialized && isStoreHydrated) {
      // 初回起動時は自動選択しない（自動分析を防ぐ）
      setIsInitialized(true)
    }
  }, [consultationStore.personaSettings, consultationStore.getCachedAnalysis, isInitialized, characterIcons, isStoreHydrated])
  
  // アイコンの変更を監視
  useEffect(() => {
    if (selectedCharacterId && selectedCharacterId !== 'none' && characterIcons) {
      const icon = consultationStore.getCharacterIcon(selectedCharacterId)
      if (icon !== characterIcon) {
        setCharacterIcon(icon)
      }
    }
  }, [selectedCharacterId, characterIcons, consultationStore, characterIcon])
  
  const handlePersonaChange = async (characterId: string) => {
    setSelectedCharacterId(characterId)
    
    if (characterId === 'none') {
      setPersonaEnabled(false)
      consultationStore.clearPersona()
      setAnalysisReasoning(null)
      setCharacterIcon(null)
    } else {
      // アイコンをロード
      const icon = consultationStore.getCharacterIcon(characterId)
      setCharacterIcon(icon)
      
      const character = characterStore.characters?.find(c => c.id === characterId)
      if (character) {
        setPersonaEnabled(true)
        
        // キャッシュを確認
        const cached = consultationStore.getCachedAnalysis(characterId)
        if (cached) {
          // キャッシュがあればそれを使用
          setSettings(cached.settings)
          setAnalysisReasoning(cached.reasoning)
          
          console.log('PersonaSelector sending characterData:', {
            name: character.result.name,
            hasBehavior: !!character.result.behavior,
            behaviorFirstPerson: character.result.behavior?.firstPerson,
            hasDirectSpeechPattern: !!character.result.behavior?.speechPattern,
            keys: Object.keys(character.result || {})
          })
          
          const personaSettings: PersonaSettings = {
            characterId,
            characterName: character.result.name || 'キャラクター',
            enabled: true,
            maintainPerspective,
            settings: cached.settings,
            characterData: character.result
          }
          
          consultationStore.setPersona(personaSettings)
        } else {
          // キャッシュがなければAI分析
          setIsAnalyzing(true)
          
          try {
            const response = await apiClient.analyzePersona(character.result)
            
            if (response.success && response.data) {
              const { reasoning, ...autoSettings } = response.data
              setSettings(autoSettings)
              setAnalysisReasoning(reasoning)
              
              // 成功時のみキャッシュに保存
              consultationStore.setCachedAnalysis(characterId, {
                settings: autoSettings,
                reasoning
              })
              
              console.log('PersonaSelector (after AI analysis) sending characterData:', {
                name: character.result.name,
                hasBehavior: !!character.result.behavior,
                behaviorFirstPerson: character.result.behavior?.firstPerson,
                hasDirectSpeechPattern: !!character.result.behavior?.speechPattern,
                keys: Object.keys(character.result || {})
              })
              
              const personaSettings: PersonaSettings = {
                characterId,
                characterName: character.result.name || 'キャラクター',
                enabled: true,
                maintainPerspective,
                settings: autoSettings,
                characterData: character.result
              }
              
              consultationStore.setPersona(personaSettings)
            } else {
              // エラー時はデフォルト値を使用（キャッシュには保存しない）
              setSettings(DEFAULT_SETTINGS)
              setAnalysisReasoning(null)
              
              const personaSettings: PersonaSettings = {
                characterId,
                characterName: character.result.name || 'キャラクター',
                enabled: true,
                maintainPerspective,
                settings: DEFAULT_SETTINGS,
                characterData: character.result
              }
              
              consultationStore.setPersona(personaSettings)
            }
          } catch (error) {
            console.error('AI分析エラー:', error)
            // エラー時はデフォルト値を使用（キャッシュには保存しない）
            setSettings(DEFAULT_SETTINGS)
            setAnalysisReasoning(null)
            
            const personaSettings: PersonaSettings = {
              characterId,
              characterName: character.result.name || 'キャラクター',
              enabled: true,
              maintainPerspective,
              settings: DEFAULT_SETTINGS,
              characterData: character.result
            }
            
            consultationStore.setPersona(personaSettings)
          } finally {
            setIsAnalyzing(false)
          }
        }
      }
    }
  }
  
  // パラメータの説明を取得
  const getParameterDescription = (param: keyof PersonaSettings['settings'], value: number): string => {
    switch (param) {
      case 'personalityStrength':
        if (value >= 8) return '非常に個性的'
        if (value >= 5) return 'バランス型'
        return '控えめ'
      case 'formality':
        if (value >= 8) return '非常に丁寧'
        if (value >= 5) return '適度に丁寧'
        if (value >= 3) return 'カジュアル'
        return 'くだけた口調'
      case 'creativity':
        if (value >= 8) return '非常に創造的'
        if (value >= 5) return 'バランス型'
        return '実用的'
      case 'empathy':
        if (value >= 8) return '非常に共感的'
        if (value >= 5) return 'バランス型'
        return '論理優先'
      case 'subjectivity':
        if (value >= 8) return '非常に主観的'
        if (value >= 5) return 'バランス型'
        return '客観的'
      default:
        return ''
    }
  }
  
  const handleEnabledChange = (checked: boolean) => {
    setPersonaEnabled(checked)
    
    if (checked && selectedCharacterId !== 'none') {
      handlePersonaChange(selectedCharacterId)  // これは自動判定を含む
    } else {
      consultationStore.clearPersona()
    }
  }
  
  // AI分析パラメータを再生成
  const handleRegenerateAnalysis = async () => {
    if (selectedCharacterId === 'none' || !personaEnabled) return
    
    const character = characterStore.characters?.find(c => c.id === selectedCharacterId)
    if (!character) return
    
    setIsAnalyzing(true)
    setAnalysisReasoning(null)
    
    try {
      // キャッシュをクリア
      consultationStore.setCachedAnalysis(selectedCharacterId, null)
      
      // AI分析を再実行
      const response = await apiClient.analyzePersona(character.result)
      
      if (response.success && response.data) {
        const { reasoning, ...autoSettings } = response.data
        setSettings(autoSettings)
        setAnalysisReasoning(reasoning)
        
        // 成功時のみキャッシュに保存
        consultationStore.setCachedAnalysis(selectedCharacterId, {
          settings: autoSettings,
          reasoning
        })
        
        const personaSettings: PersonaSettings = {
          characterId: selectedCharacterId,
          characterName: character.result.name || 'キャラクター',
          enabled: true,
          maintainPerspective,
          settings: autoSettings,
          characterData: character.result
        }
        
        consultationStore.setPersona(personaSettings)
      } else {
        // エラー時はデフォルト値を使用（キャッシュには保存しない）
        setSettings(DEFAULT_SETTINGS)
        setAnalysisReasoning(null)
        
        const personaSettings: PersonaSettings = {
          characterId: selectedCharacterId,
          characterName: character.result.name || 'キャラクター',
          enabled: true,
          maintainPerspective,
          settings: DEFAULT_SETTINGS,
          characterData: character.result
        }
        
        consultationStore.setPersona(personaSettings)
      }
    } catch (error) {
      console.error('AI分析エラー:', error)
      // エラー時はデフォルト値を使用（キャッシュには保存しない）
      setSettings(DEFAULT_SETTINGS)
      setAnalysisReasoning(null)
      
      const personaSettings: PersonaSettings = {
        characterId: selectedCharacterId,
        characterName: character.result.name || 'キャラクター',
        enabled: true,
        maintainPerspective,
        settings: DEFAULT_SETTINGS,
        characterData: character.result
      }
      
      consultationStore.setPersona(personaSettings)
    } finally {
      setIsAnalyzing(false)
    }
  }
  
  const handleIconUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && selectedCharacterId !== 'none') {
      const reader = new FileReader()
      reader.onload = (event) => {
        const base64 = event.target?.result as string
        setCharacterIcon(base64)
        consultationStore.setCharacterIcon(selectedCharacterId, base64)
      }
      reader.readAsDataURL(file)
    }
  }
  
  const handleIconRemove = () => {
    if (selectedCharacterId !== 'none') {
      setCharacterIcon(null)
      consultationStore.clearCharacterIcon(selectedCharacterId)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }
  
  return (
    <div className="space-y-4">
      {/* ペルソナ有効化 */}
      <div className="flex items-center space-x-2">
        <Checkbox
          id="enablePersona"
          checked={personaEnabled}
          onCheckedChange={handleEnabledChange}
        />
        <Label htmlFor="enablePersona" className="text-sm cursor-pointer">
          キャラクターペルソナを使用
        </Label>
      </div>
      
      {/* キャラクター選択 */}
      <div className="space-y-2">
        <Label className="text-xs">相談相手のキャラクター</Label>
        {!characterStore.characters || characterStore.characters.length === 0 ? (
          <div className="text-xs text-muted-foreground p-2 border rounded">
            保存済みキャラクターがありません。キャラクタージェネレータでキャラクターを作成してください。
          </div>
        ) : (
          <Select
            value={selectedCharacterId}
            onValueChange={handlePersonaChange}
            disabled={!personaEnabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="キャラクターを選択..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">なし</SelectItem>
              {/* お気に入りを上部に表示 */}
              {characterStore.characters
                ?.slice()
                .sort((a, b) => {
                  if (a.favorite && !b.favorite) return -1
                  if (!a.favorite && b.favorite) return 1
                  return 0
                })
                .map((character) => (
                  <SelectItem key={character.id} value={character.id}>
                    <span className="flex items-center gap-1">
                      {character.favorite && <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />}
                      {character.result.name || 'Unknown'}
                    </span>
                  </SelectItem>
                )) || null}
            </SelectContent>
          </Select>
        )}
        {/* お気に入りトグル */}
        {personaEnabled && selectedCharacterId !== 'none' && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => characterStore.toggleFavorite(selectedCharacterId)}
            className="h-7 px-2 text-xs"
          >
            <Star className={`h-3 w-3 mr-1 ${characterStore.isFavorite(selectedCharacterId) ? 'fill-yellow-400 text-yellow-400' : ''}`} />
            {characterStore.isFavorite(selectedCharacterId) ? 'お気に入り解除' : 'お気に入り登録'}
          </Button>
        )}
      </div>
      
      {/* アイコン設定 */}
      {personaEnabled && selectedCharacterId !== 'none' && (
        <div className="space-y-2">
          <Label className="text-xs">キャラクターアイコン</Label>
          <div className="flex items-center gap-3">
            {characterIcon ? (
              <div className="relative">
                <img 
                  src={characterIcon} 
                  alt="Character Icon" 
                  className="w-16 h-16 rounded-full object-cover border-2 border-border"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute -top-1 -right-1 h-5 w-5"
                  onClick={handleIconRemove}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center border-2 border-dashed border-border">
                <User className="h-8 w-8 text-muted-foreground" />
              </div>
            )}
            <div className="flex flex-col gap-1">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleIconUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-3 w-3 mr-1" />
                アイコンを選択
              </Button>
              <p className="text-xs text-muted-foreground">
                JPG, PNG, GIF (最大2MB)
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* 詳細設定 */}
      {personaEnabled && selectedCharacterId !== 'none' && (
        <div className="space-y-3">
          <div className="text-xs font-medium">ペルソナ設定</div>
          
          {/* キャラクター視点の維持 */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="maintainPerspective"
              checked={maintainPerspective}
              onCheckedChange={(checked) => {
                const newValue = checked as boolean
                setMaintainPerspective(newValue)
                
                // ペルソナ設定を更新
                if (personaEnabled && selectedCharacterId !== 'none') {
                  const character = characterStore.characters?.find(c => c.id === selectedCharacterId)
                  if (character) {
                    const personaSettings: PersonaSettings = {
                      characterId: selectedCharacterId,
                      characterName: character.result.name || 'キャラクター',
                      enabled: true,
                      maintainPerspective: newValue,  // 新しい値を使用
                      settings,
                      characterData: character.result
                    }
                    consultationStore.setPersona(personaSettings)
                  }
                }
              }}
            />
            <Label htmlFor="maintainPerspective" className="text-xs cursor-pointer">
              キャラクターの視点を維持
            </Label>
          </div>
          
          {/* 自動判定されたパラメータの表示 */}
          <div className="p-3 bg-muted/50 rounded-md space-y-2">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium">AI分析パラメータ</span>
                {isAnalyzing && <Loader2 className="h-3 w-3 animate-spin" />}
              </div>
              <Button
                size="icon"
                variant="ghost"
                onClick={handleRegenerateAnalysis}
                disabled={isAnalyzing}
                title="AI分析を再生成"
                className="h-6 w-6"
              >
                <RefreshCw className="h-3 w-3" />
              </Button>
            </div>
            
            {/* 性格の強さ */}
            <div className="flex justify-between items-center">
              <Label className="text-xs">性格の強さ</Label>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium">{settings.personalityStrength}/10</span>
                <span className="text-xs text-muted-foreground">({getParameterDescription('personalityStrength', settings.personalityStrength)})</span>
              </div>
            </div>
            
            {/* 口調の丁寧さ */}
            <div className="flex justify-between items-center">
              <Label className="text-xs">口調の丁寧さ</Label>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium">{settings.formality}/10</span>
                <span className="text-xs text-muted-foreground">({getParameterDescription('formality', settings.formality)})</span>
              </div>
            </div>
            
            {/* 創造性 */}
            <div className="flex justify-between items-center">
              <Label className="text-xs">創造性</Label>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium">{settings.creativity}/10</span>
                <span className="text-xs text-muted-foreground">({getParameterDescription('creativity', settings.creativity)})</span>
              </div>
            </div>
            
            {/* 共感性 */}
            <div className="flex justify-between items-center">
              <Label className="text-xs">共感性</Label>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium">{settings.empathy}/10</span>
                <span className="text-xs text-muted-foreground">({getParameterDescription('empathy', settings.empathy)})</span>
              </div>
            </div>
            
            {/* 主観性 */}
            <div className="flex justify-between items-center">
              <Label className="text-xs">主観性</Label>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium">{settings.subjectivity}/10</span>
                <span className="text-xs text-muted-foreground">({getParameterDescription('subjectivity', settings.subjectivity)})</span>
              </div>
            </div>
            
            {/* AI分析理由の表示 */}
            {analysisReasoning && (
              <details className="mt-2 pt-2 border-t">
                <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                  AI分析の詳細を見る
                </summary>
                <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                  {Object.entries(analysisReasoning).map(([key, reason]) => {
                    const label = {
                      personalityStrength: '性格の強さ',
                      formality: '口調の丁寧さ',
                      creativity: '創造性',
                      empathy: '共感性',
                      subjectivity: '主観性'
                    }[key]
                    return (
                      <div key={key}>
                        <span className="font-medium">{label}: </span>
                        <span>{reason}</span>
                      </div>
                    )
                  })}
                </div>
              </details>
            )}
            
            <div className="text-xs text-muted-foreground mt-2">
              ※ Gemini AIがキャラクターを分析して自動判定しました
            </div>
          </div>
        </div>
      )}
    </div>
  )
}